import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import { SvgIconProps, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import React, { ComponentType, useCallback, useContext, useRef, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import { Job, JobInvoicing } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import ApprovalActionsMenu from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobsQueryContext from '../../OrdersList/ExpandedBlock/JobsQueryContext';
import { FlexColumn } from '../../styled';
import { ActionsMenuItem, ActionsMenuTrigger } from './ActionsMenu';

interface ActionsColumnMenuAction {
  icon: ComponentType<SvgIconProps>;
  key: string;
  label: string;
  onSelect: () => void;
}

interface ActionsColumnProps {
  job: JobInvoicing;
  onJobUpdated?: (job: JobInvoicing) => void;
  onViewJobCardPdf: (job: Job) => Promise<void>;
  isGeneratingJobCardPdf: boolean;
  isMenuBusy?: boolean;
  menuActions?: ActionsColumnMenuAction[];
  primaryAction?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export default function ActionsColumn({
  job,
  onJobUpdated,
  onViewJobCardPdf,
  isGeneratingJobCardPdf,
  isMenuBusy = false,
  menuActions = [],
  primaryAction,
  sx,
}: ActionsColumnProps) {
  const menuHandler = useRef<MenuHandler>(null);
  const { userStore } = useStores();
  const { canApproveJob } = userStore.getPermissions();
  const { approveJob, getJobById, unapproveJob } = useJob();
  const { updateJobInQueryData } = useContext(JobsQueryContext);
  const companyId = job.company?.id ?? getEffectiveUserCompanyId(userStore);

  const [isBusy, setIsBusy] = useState(false);

  const { id, jobId } = job;
  const {
    isApproved: jobApproved,
    isUnapprovable: jobUnapprovable,
    isApprovable: jobApprovable,
  } = getApprovalStatus(job, userStore.getPermissions(), Boolean(job.order?.editable));
  const approveDisabledReason = jobApprovable
    ? undefined
    : t('approvals.approve_jobs.approve_blocked');

  const applyJobUpdate = useCallback(
    (updatedJob: JobInvoicing) => {
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      } else {
        updateJobInQueryData(updatedJob);
      }
    },
    [onJobUpdated, updateJobInQueryData],
  );

  const handleUnapproveJobClick = useCallback(async () => {
    if (jobUnapprovable) {
      try {
        setIsBusy(true);

        const updatedJob = await unapproveJob(id, companyId);

        if (updatedJob) {
          applyJobUpdate(updatedJob);
        }
      } finally {
        setIsBusy(false);
      }
    }
  }, [id, companyId, jobUnapprovable, applyJobUpdate, unapproveJob]);

  const handleApproveJobClick = useCallback(async () => {
    try {
      setIsBusy(true);

      const updatedJob = await approveJob(id, companyId);

      if (updatedJob) {
        trackApprovalBatch('job', 1);
        applyJobUpdate(updatedJob);
      }
    } catch (error) {
      const rowIsStale = isAxiosError(error) && error.response?.status === 422;
      const serverJob = rowIsStale
        ? await getJobById(id, companyId).catch(() => null)
        : null;

      if (serverJob) {
        applyJobUpdate(
          JobInvoicing.fromJobOrJobInvoicingAndInvoices(
            serverJob,
            job.receivableInvoice,
            job.payableInvoice,
          ),
        );
      }
    } finally {
      setIsBusy(false);
    }
  }, [
    id,
    companyId,
    applyJobUpdate,
    approveJob,
    getJobById,
    job.receivableInvoice,
    job.payableInvoice,
  ]);

  let content: React.ReactNode = primaryAction ?? null;

  if (canApproveJob && !primaryAction) {
    if (jobApproved && jobUnapprovable) {
      content = (
        <ApprovalActionsMenu
          approved
          isLoading={isBusy}
          onMenuItemClick={handleUnapproveJobClick}
        />
      );
    }

    if (!jobApproved) {
      const approveButton = (
        <SmallLoadingButton
          color="success"
          disabled={Boolean(approveDisabledReason)}
          loading={isBusy}
          onClick={handleApproveJobClick}
          sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGreen })}
        >
          {t('actions.approve')}
        </SmallLoadingButton>
      );

      content = approveDisabledReason ? (
        <Tooltip title={approveDisabledReason}>
          <span data-test-id="approve-disabled-tooltip">{approveButton}</span>
        </Tooltip>
      ) : (
        approveButton
      );
    }
  }

  if (!content) {
    content = (
      <Chip
        color={jobApproved ? 'success' : 'secondary'}
        hideDropdownArrow={true}
        label={jobApproved ? 'Approved' : 'Pending'}
      />
    );
  }

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        alignItems: 'flex-start',
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-end',
        ...sx,
      }}
    >
      <FlexColumn>
        {content}

        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray3,
            fontSize: '10px',
            textAlign: 'right',
          })}
        >
          {jobId}
        </Typography>
      </FlexColumn>
      <Menu
        ref={menuHandler}
        menuTrigger={<ActionsMenuTrigger isBusy={isGeneratingJobCardPdf || isMenuBusy} />}
      >
        <ActionsMenuItem
          dataUi={`page-approve-jobs__actions__job-${jobId}__view-pdf`}
          icon={PictureAsPdf}
          label={t('dispatch.job_card.view_pdf')}
          onClick={(event) => {
            event.stopPropagation();
            onViewJobCardPdf(job);
            menuHandler.current?.onClose?.();
          }}
        />
        {menuActions.map((action) => (
          <ActionsMenuItem
            key={action.key}
            icon={action.icon}
            label={action.label}
            onClick={(event) => {
              event.stopPropagation();
              action.onSelect();
              menuHandler.current?.onClose?.();
            }}
          />
        ))}
      </Menu>
    </Box>
  );
}
